/**
 * SurveySettings Component
 *
 * Reusable settings container with tabbed interface for organizing survey settings.
 * Used in the "Settings" tab of survey creation/editing to group related settings.
 *
 * ## Used By
 * - CustomSurvey (Basic Info, Confirmation Settings, Date Range)
 * - EventRegistrationSurvey (Event Details, Attendees, Payments)
 * - SatisfactionSurvey (Basic Info, Review Request, Notifications)
 * - DonationSurvey (Page Setup, Donor Info, Donation Options)
 *
 * ## Features
 * - **Horizontal Tab Navigation**: Switches between setting groups
 * - **Optional Header**: Title and subtitle support
 * - **Auto-selects First Tab**: Defaults to first tab on mount
 * - **Custom Styling**: Supports style overrides via props
 * - **Flexible Content**: Each tab can contain any React content
 *
 * ## How It Works
 * 1. Pass array of tab objects (title, key, content)
 * 2. Component renders tab navigation at top
 * 3. Content area shows active tab's content
 * 4. User clicks tabs to switch between setting groups
 *
 * @example Basic usage in CustomSurvey
 * ```tsx
 * <SurveySettings
 *   title="Survey Settings"
 *   subtitle="Configure your survey options"
 *   tabs={[
 *     {
 *       key: 'basic',
 *       title: 'Basic Information',
 *       content: <BasicInfoSettings />
 *     },
 *     {
 *       key: 'confirmation',
 *       title: 'Confirmation',
 *       content: <ConfirmationSettings />
 *     }
 *   ]}
 * />
 * ```
 */

import { css, StyleDeclaration } from 'aphrodite';
import { useState } from 'react';
import { BasicTabList, BasicTabTrigger, TabContent, TabRoot } from '../../../../../components/Tabs';
import { styleSheet } from './style';

/**
 * Tab item definition
 */
type TabItem = {
	/** Display text for tab button */
	title: string;
	/** Unique identifier for tab (used for selection state) */
	key: string;
	/** React content to display when tab is active */
	content: React.ReactNode;
};

/**
 * Props for SurveySettings component
 */
type Props = {
	/** Optional title displayed above tabs */
	title?: string;
	/** Optional subtitle/description displayed below title */
	subtitle?: React.ReactNode;
	/** Array of tab configurations (title, key, content) */
	tabs: TabItem[];
	/** Custom styles for outer container */
	styles?: StyleDeclaration[];
	/** Custom styles for tab list navigation */
	tabListStyles?: StyleDeclaration[];
	/** Custom styles for TabRoot wrapper */
	tabRootStyles?: StyleDeclaration[];
};

/**
 * SurveySettings component implementation
 */
export const SurveySettings = ({
	tabs,
	title,
	subtitle,
	styles = [],
	tabListStyles = [],
	tabRootStyles = [],
}: Props) => {
	// Auto-select first tab on mount (defaults to empty string if no tabs)
	const [selectedTab, setSelectedTab] = useState(tabs.length > 0 ? tabs[0].key : '');

	return (
		<div className={css(styleSheet.settingsContainer, ...styles)}>
			{title && <h1 className={css(styleSheet.title)}>{title}</h1>}
			{subtitle && <p className={css(styleSheet.subtitle)}>{subtitle}</p>}

			<TabRoot value={selectedTab} onValueChange={setSelectedTab} className={css(styleSheet.tabRoot, ...tabRootStyles)}>
				<div className={css(styleSheet.tabListContainer)}>
					<BasicTabList className={css(styleSheet.tabList, ...tabListStyles)}>
						{tabs.map(tab => (
							<BasicTabTrigger key={`${tab.key}-tab-trigger`} value={tab.key} className={css(styleSheet.tabTrigger)}>
								{tab.title}
							</BasicTabTrigger>
						))}
					</BasicTabList>
				</div>
				<div className={css(styleSheet.tabContentContainer)}>
					{tabs.map(tab => (
						<TabContent
							key={`${tab.key}-tab-content`}
							value={tab.key}
							forceMount
							className={css(selectedTab !== tab.key && styleSheet.tabContentHidden)}
						>
							{tab.content}
						</TabContent>
					))}
				</div>
			</TabRoot>
		</div>
	);
};
